import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import {
  Clear as ClearIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Color palette
const T = {
  blue900: "#0C2D6B",
  blue800: "#1044A3",
  blue700: "#1557C0",
  blue600: "#1A6FDB",
  blue500: "#2684F5",
  blue100: "#DBEAFE",
  blue50: "#EFF6FF",
  green700: "#166534",
  green600: "#16A34A",
  green500: "#22C55E",
  green100: "#DCFCE7",
  green50: "#F0FDF4",
  white: "#FFFFFF",
  gray50: "#F8FAFF",
  gray300: "#C7D4F0",
  border: "rgba(26,111,219,0.14)",
  textPrimary: "#0C2D6B",
  textSecondary: "#4A6082",
  textMuted: "#8FA5CC",
};

// Custom Card Component
const DashboardCard = ({ children, sx = {} }) => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: T.white,
      border: `1px solid ${T.border}`,
      borderRadius: 4,
      boxShadow:
        "0 4px 24px rgba(26,111,219,0.08), 0 1px 4px rgba(26,111,219,0.06)",
      p: { xs: 2.5, sm: 3.5 },
      transition: "box-shadow 0.3s ease",
      "&:hover": {
        boxShadow: "0 8px 32px rgba(26,111,219,0.12)",
      },
      ...sx,
    }}
  >
    {children}
  </Paper>
);

// Stat Card Component
const StatCard = ({
  label = "",
  value = 0,
  icon = "📁",
  color = T.blue600,
  bg = T.blue50,
}) => (
  <Card
    elevation={0}
    sx={{
      bgcolor: T.white,
      border: `1px solid ${T.border}`,
      borderRadius: 4,
      boxShadow:
        "0 4px 24px rgba(26,111,219,0.08), 0 1px 4px rgba(26,111,219,0.06)",
      transition: "transform 0.25s ease, box-shadow 0.25s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 20px 48px rgba(26,111,219,0.18)",
      },
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 28, sm: 34 },
              fontWeight: 800,
              fontFamily: "'Playfair Display', serif",
              color: T.textPrimary,
              lineHeight: 1,
              letterSpacing: "-0.5px",
            }}
          >
            {value}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: T.textSecondary,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              mt: 1.5,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box
          sx={{
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 },
            borderRadius: 3,
            bgcolor: bg,
            border: `1px solid ${color}30`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography component="span" sx={{ fontSize: 24, color }}>
            {icon}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          mt: 2.5,
          height: 4,
          borderRadius: 2,
          background: `linear-gradient(90deg, ${color}, ${color}30)`,
          width: "60%",
        }}
      />
    </CardContent>
  </Card>
);

// Section Title Component
const SectionTitle = ({ title, subtitle, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: color || T.blue600,
      }}
    />
    <Box>
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 800,
          color: T.textPrimary,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            fontSize: 12,
            color: T.textMuted,
            mt: 0.2,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

// Custom Tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        sx={{
          p: 2,
          bgcolor: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: 2,
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          fontFamily: "'Plus Jakarta Sans',sans-serif",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: T.textPrimary, fontWeight: 800, mb: 1 }}
        >
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 3,
              mb: 0.5,
            }}
          >
            <Typography
              sx={{ fontSize: 13, color: entry.color, fontWeight: 600 }}
            >
              {entry.name}:
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: T.textPrimary, fontWeight: 700 }}
            >
              {entry.value.toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }
  return null;
};

// Safe property getter to handle data mapping between forms/API
const getProp = (obj, keys) => {
  for (let key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return "Unknown";
};

const DashboardPortal = ({ reports = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Filters
  const [selectedQuarter, setSelectedQuarter] = useState("");

  // Extract unique quarters for the filter dropdown
  const uniqueQuarters = useMemo(() => {
    const quarters = reports
      .map((r) => getProp(r, ["Quarter", "quarter"]))
      .filter((q) => q && q !== "Unknown");
    return [...new Set(quarters)].sort();
  }, [reports]);

  // Filter reports by quarter
  const filteredReports = useMemo(() => {
    if (!selectedQuarter) return reports;
    return reports.filter(
      (r) => getProp(r, ["Quarter", "quarter"]) === selectedQuarter,
    );
  }, [reports, selectedQuarter]);

  // Calculate high-level KPIs
  const kpis = useMemo(() => {
    let totalApps = 0;
    let totalSuccess = 0;
    let totalVacancies = 0;

    filteredReports.forEach((r) => {
      totalApps += Number(r.applications || 0);
      totalSuccess += Number(r.successfulCandidates || 0);
      totalVacancies += Number(r.vacancyNumber || 0);
    });

    const successRate =
      totalApps > 0 ? ((totalSuccess / totalApps) * 100).toFixed(1) : 0;

    return { totalApps, totalSuccess, totalVacancies, successRate };
  }, [filteredReports]);

  // Helper function to aggregate data
  const aggregateData = (keyMappings) => {
    const map = {};
    filteredReports.forEach((r) => {
      const key = getProp(r, keyMappings);
      if (!map[key]) {
        map[key] = { name: key, Applications: 0, Successes: 0 };
      }
      map[key].Applications += Number(r.applications || 0);
      map[key].Successes += Number(r.successfulCandidates || 0);
    });
    return Object.values(map).sort((a, b) => b.Applications - a.Applications);
  };

  // Build Chart Data
  const companyData = useMemo(
    () => aggregateData(["HiringCompany", "hiringCompany"]),
    [filteredReports],
  );
  const sectorData = useMemo(
    () => aggregateData(["ISICEconomicSector", "sector"]),
    [filteredReports],
  );
  const positionData = useMemo(
    () => aggregateData(["ISCOPositionCategory", "positionCategory"]),
    [filteredReports],
  );

  // Build Demographics Data (Gender & Age)
  const demographicsData = useMemo(() => {
    let mYApps = 0,
      mAApps = 0,
      fYApps = 0,
      fAApps = 0;
    let mYSucc = 0,
      mASucc = 0,
      fYSucc = 0,
      fASucc = 0;

    filteredReports.forEach((r) => {
      mYApps += Number(r.applicationsMaleYouth || 0);
      mAApps += Number(r.applicationsMaleAdult || 0);
      fYApps += Number(r.applicationsFemaleYouth || 0);
      fAApps += Number(r.applicationsFemaleAdult || 0);

      mYSucc += Number(r.successfulMaleYouth || 0);
      mASucc += Number(r.successfulMaleAdult || 0);
      fYSucc += Number(r.successfulFemaleYouth || 0);
      fASucc += Number(r.successfulFemaleAdult || 0);
    });

    return [
      { name: "Male (Youth)", Applications: mYApps, Successes: mYSucc },
      { name: "Male (Adult)", Applications: mAApps, Successes: mASucc },
      { name: "Female (Youth)", Applications: fYApps, Successes: fYSucc },
      { name: "Female (Adult)", Applications: fAApps, Successes: fAApps },
    ];
  }, [filteredReports]);

  if (!Array.isArray(reports)) {
    return (
      <DashboardCard>
        <Typography color="error" sx={{ p: 4, textAlign: "center" }}>
          Invalid data format. Expected an array of reports.
        </Typography>
      </DashboardCard>
    );
  }

  if (reports.length === 0) {
    return (
      <DashboardCard>
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography sx={{ color: T.textMuted, fontSize: 16 }}>
            No reports available yet. Create a report to see dashboard metrics.
          </Typography>
        </Box>
      </DashboardCard>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 5,
              height: 36,
              borderRadius: 1,
              background: `linear-gradient(180deg, ${T.blue600}, ${T.green500})`,
            }}
          />
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontFamily: "'Playfair Display', serif",
                color: T.blue900,
                lineHeight: 1.1,
                fontSize: { xs: "1.8rem", sm: "2.125rem" },
              }}
            >
              Analytics Dashboard
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: T.textMuted,
                fontWeight: 500,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                mt: 0.5,
              }}
            >
              Recruitment statistics and metrics overview
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => alert("Export functionality coming soon")}
          sx={{
            color: T.blue600,
            borderColor: T.blue600,
            borderRadius: 2,
            "&:hover": { borderColor: T.blue700, bgcolor: T.blue50 },
          }}
        >
          Export Report
        </Button>
      </Box>

      {/* Filter Section */}
      <DashboardCard sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              Filter by Quarter
            </InputLabel>
            <Select
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
              label="Filter by Quarter"
              sx={{
                borderRadius: 2,
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: 14,
              }}
            >
              <MenuItem
                value=""
                sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}
              >
                All Quarters
              </MenuItem>
              {uniqueQuarters.map((q) => (
                <MenuItem
                  key={q}
                  value={q}
                  sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}
                >
                  {q}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedQuarter && (
            <Button
              variant="text"
              onClick={() => setSelectedQuarter("")}
              startIcon={<ClearIcon />}
              sx={{
                color: T.textSecondary,
                textTransform: "none",
                fontFamily: "'Plus Jakarta Sans',sans-serif",
              }}
            >
              Clear Filter
            </Button>
          )}

          <Box sx={{ ml: "auto" }}>
            <Chip
              label={`${filteredReports.length} Reports Found`}
              color="primary"
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans',sans-serif",
              }}
            />
          </Box>
        </Box>
      </DashboardCard>

      {/* KPI Stat Cards */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total Applications"
            value={kpis.totalApps.toLocaleString()}
            icon="📥"
            color={T.blue600}
            bg={T.blue100}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total Successes"
            value={kpis.totalSuccess.toLocaleString()}
            icon="🎉"
            color={T.green600}
            bg={T.green100}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total Vacancies"
            value={kpis.totalVacancies.toLocaleString()}
            icon="💼"
            color={T.blue800}
            bg={T.blue50}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Success Rate"
            value={`${kpis.successRate}%`}
            icon="📈"
            color={T.green700}
            bg={T.green50}
          />
        </Grid>
      </Grid>

      {/* Chart 1: Applications vs Success by Company */}
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <DashboardCard>
            <SectionTitle
              title="Recruitment by Company"
              subtitle="Comparing total applications and successful hires per organization"
              color={T.blue600}
            />
            <Box sx={{ width: "100%", height: 400 }}>
              {companyData.length > 0 ? (
                <ResponsiveContainer>
                  <BarChart
                    data={companyData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.gray300}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      angle={-35}
                      textAnchor="end"
                      height={80}
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 12,
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 12,
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                        fontSize: 13,
                        paddingTop: 20,
                      }}
                    />
                    <Bar
                      dataKey="Applications"
                      fill={T.blue500}
                      radius={[4, 4, 0, 0]}
                      barSize={isMobile ? 15 : 30}
                    />
                    <Bar
                      dataKey="Successes"
                      fill={T.green500}
                      radius={[4, 4, 0, 0]}
                      barSize={isMobile ? 15 : 30}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography color={T.textMuted}>
                    No company data available
                  </Typography>
                </Box>
              )}
            </Box>
          </DashboardCard>
        </Grid>

        {/* Chart 2: Demographics (Gender & Age) */}
        <Grid item xs={12}>
          <DashboardCard>
            <SectionTitle
              title="Demographics Breakdown"
              subtitle="Applications and successes grouped by gender and age category"
              color={T.green600}
            />
            <Box sx={{ width: "100%", height: 350 }}>
              <ResponsiveContainer>
                <BarChart
                  data={demographicsData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={T.gray300}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: T.textSecondary,
                      fontSize: 12,
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                    }}
                  />
                  <YAxis
                    tick={{
                      fill: T.textSecondary,
                      fontSize: 12,
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: 13,
                    }}
                  />
                  <Bar
                    dataKey="Applications"
                    fill={T.blue400}
                    radius={[4, 4, 0, 0]}
                    barSize={isMobile ? 20 : 40}
                  />
                  <Bar
                    dataKey="Successes"
                    fill={T.green400}
                    radius={[4, 4, 0, 0]}
                    barSize={isMobile ? 20 : 40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </DashboardCard>
        </Grid>

        {/* Chart 3: Sector Distribution */}
        <Grid item xs={12} md={6}>
          <DashboardCard>
            <SectionTitle
              title="Data by Economic Sector"
              subtitle="Applications and successes per sector"
              color={T.blue800}
            />
            <Box sx={{ width: "100%", height: 350 }}>
              {sectorData.length > 0 ? (
                <ResponsiveContainer>
                  <BarChart
                    data={sectorData}
                    margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.gray300}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 11,
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 11,
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                        fontSize: 12,
                      }}
                    />
                    <Bar
                      dataKey="Applications"
                      fill={T.blue500}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="Successes"
                      fill={T.green500}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography color={T.textMuted}>
                    No sector data available
                  </Typography>
                </Box>
              )}
            </Box>
          </DashboardCard>
        </Grid>

        {/* Chart 4: Position Category Distribution */}
        <Grid item xs={12} md={6}>
          <DashboardCard>
            <SectionTitle
              title="Data by Position Category"
              subtitle="ISCO Classifications"
              color={T.blue500}
            />
            <Box sx={{ width: "100%", height: 350 }}>
              {positionData.length > 0 ? (
                <ResponsiveContainer>
                  <BarChart
                    data={positionData}
                    margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.gray300}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 11,
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 11,
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                        fontSize: 12,
                      }}
                    />
                    <Bar
                      dataKey="Applications"
                      fill={T.blue600}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="Successes"
                      fill={T.green600}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography color={T.textMuted}>
                    No position data available
                  </Typography>
                </Box>
              )}
            </Box>
          </DashboardCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPortal;
